import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import TransformIcon from '@mui/icons-material/Transform';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import type { PipelineTargetTable, PipelineJoinNode } from './types';
import { SOURCE_COLOR, JOIN_COLOR, TARGET_COLOR, EXPR_COLOR, DIRECT_COLOR } from './types';

interface TargetChainPanelProps {
  target: PipelineTargetTable;
  joins: PipelineJoinNode[];
  /** When true, removes outer border/bgcolor for embedding inside another panel */
  embedded?: boolean;
}

/* ─── Arrow between stages ─────────────────────────────── */
const StageArrow: React.FC = () => (
  <ArrowForwardIcon sx={{ fontSize: 20, color: '#cbd5e1', flexShrink: 0, mx: 0.5 }} />
);

/* ─── Stage card wrapper ───────────────────────────────── */
const StageCard: React.FC<{
  color: string;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ color, icon, title, children }) => (
  <Box
    sx={{
      minWidth: 160,
      maxWidth: 280,
      bgcolor: '#fff',
      border: `1.5px solid ${color}40`,
      borderRadius: '10px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      overflow: 'hidden',
      flexShrink: 0,
    }}
  >
    <Box
      sx={{
        bgcolor: `${color}10`,
        px: 1.25,
        py: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        borderBottom: `1px solid ${color}20`,
      }}
    >
      {icon}
      <Typography variant="caption" fontWeight={700} sx={{ fontSize: 10, color, textTransform: 'uppercase' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 1.25, py: 0.5 }}>{children}</Box>
  </Box>
);

const TargetChainPanel: React.FC<TargetChainPanelProps> = ({ target, joins, embedded }) => {
  // Find relevant joins for this target's sources
  const relevantJoins = joins.filter(
    (j) =>
      target.sourceTables.includes(j.leftTable) ||
      target.sourceTables.includes(j.rightTable),
  );

  // Group steps by operation type
  const directSteps = target.steps.filter((s) => s.operation === 'direct');
  const exprSteps = target.steps.filter((s) => s.operation === 'expression');
  const otherSteps = target.steps.filter(
    (s) => s.operation !== 'direct' && s.operation !== 'expression',
  );

  return (
    <Box
      sx={{
        ...(!embedded && { borderTop: '1px solid', borderColor: 'divider' }),
        bgcolor: embedded ? 'transparent' : '#fafbfc',
        flexShrink: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fff',
        }}
      >
        <Typography variant="caption" fontWeight={700} sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Transformation Chain
        </Typography>
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
          for
        </Typography>
        <Chip
          size="small"
          label={target.name}
          icon={<TableChartIcon sx={{ fontSize: '14px !important' }} />}
          sx={{
            height: 22,
            fontSize: 11,
            fontWeight: 700,
            bgcolor: `${TARGET_COLOR}14`,
            color: TARGET_COLOR,
            '& .MuiChip-icon': { color: TARGET_COLOR },
          }}
        />
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
          · {target.sourceTables.length} source{target.sourceTables.length !== 1 ? 's' : ''}
          {relevantJoins.length > 0 && ` · ${relevantJoins.length} join${relevantJoins.length !== 1 ? 's' : ''}`}
          · {target.stepCount} mappings
        </Typography>
      </Box>

      {/* Horizontal chain */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          px: 2,
          py: 1.5,
          overflowX: 'auto',
          overflowY: 'hidden',
          minHeight: 110,
        }}
      >
        {/* ── Stage 1: Source Tables ── */}
        <StageCard
          color={SOURCE_COLOR}
          icon={<StorageIcon sx={{ fontSize: 13, color: SOURCE_COLOR }} />}
          title={`${target.sourceTables.length} Source${target.sourceTables.length !== 1 ? 's' : ''}`}
        >
          {target.sourceTables.map((src) => (
            <Typography key={src} variant="caption" sx={{ fontSize: 10, color: '#475569', display: 'block', lineHeight: 1.6 }}>
              • {src}
            </Typography>
          ))}
        </StageCard>

        <StageArrow />

        {/* ── Stage 2: Joins (if any) ── */}
        {relevantJoins.length > 0 && (
          <>
            <StageCard
              color={JOIN_COLOR}
              icon={<MergeTypeIcon sx={{ fontSize: 13, color: JOIN_COLOR }} />}
              title={`${relevantJoins.length} Join${relevantJoins.length !== 1 ? 's' : ''}`}
            >
              {relevantJoins.map((j) => (
                <Box key={j.id} sx={{ mb: 0.25 }}>
                  <Typography variant="caption" sx={{ fontSize: 10, color: '#475569', display: 'block', lineHeight: 1.5 }}>
                    <strong>{j.joinType.toUpperCase()}</strong> {j.leftTable}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: 9, color: '#94a3b8', display: 'block', fontFamily: 'monospace' }}>
                    × {j.rightTable} ON {j.condition}
                  </Typography>
                </Box>
              ))}
            </StageCard>
            <StageArrow />
          </>
        )}

        {/* ── Stage 3: Direct Mappings ── */}
        {directSteps.length > 0 && (
          <>
            <StageCard
              color={DIRECT_COLOR}
              icon={<ArrowForwardIcon sx={{ fontSize: 13, color: DIRECT_COLOR }} />}
              title={`${directSteps.length} Direct`}
            >
              {directSteps.slice(0, 6).map((s) => {
                const srcCol = s.inputs[0]?.split('.').pop() ?? '?';
                const tgtCol = s.outputs[0]?.split('.').pop() ?? '?';
                return (
                  <Typography key={s.stepId} variant="caption" sx={{ fontSize: 10, color: '#475569', display: 'block', lineHeight: 1.5 }}>
                    {srcCol} → {tgtCol}
                  </Typography>
                );
              })}
              {directSteps.length > 6 && (
                <Typography variant="caption" sx={{ fontSize: 9, color: '#94a3b8', fontStyle: 'italic' }}>
                  +{directSteps.length - 6} more
                </Typography>
              )}
            </StageCard>
            <StageArrow />
          </>
        )}

        {/* ── Stage 4: Expressions ── */}
        {exprSteps.length > 0 && (
          <>
            <StageCard
              color={EXPR_COLOR}
              icon={<TransformIcon sx={{ fontSize: 13, color: EXPR_COLOR }} />}
              title={`${exprSteps.length} Expression${exprSteps.length !== 1 ? 's' : ''}`}
            >
              {exprSteps.slice(0, 5).map((s) => {
                const tgtCol = s.outputs[0]?.split('.').pop() ?? '?';
                const expr = s.expression || s.description || s.operation;
                return (
                  <Box key={s.stepId} sx={{ mb: 0.25 }}>
                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: 10, color: '#1e293b', display: 'block' }}>
                      → {tgtCol}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 9,
                        color: '#64748b',
                        fontFamily: 'monospace',
                        display: 'block',
                        lineHeight: 1.4,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 200,
                      }}
                    >
                      {expr}
                    </Typography>
                  </Box>
                );
              })}
              {exprSteps.length > 5 && (
                <Typography variant="caption" sx={{ fontSize: 9, color: '#94a3b8', fontStyle: 'italic' }}>
                  +{exprSteps.length - 5} more
                </Typography>
              )}
            </StageCard>
            <StageArrow />
          </>
        )}

        {/* ── Stage 5: Other operations (aggregate, window, pivot, etc.) ── */}
        {otherSteps.length > 0 && (
          <>
            <StageCard
              color="#f59e0b"
              icon={<TransformIcon sx={{ fontSize: 13, color: '#f59e0b' }} />}
              title={`${otherSteps.length} Other`}
            >
              {otherSteps.map((s) => {
                const tgtCol = s.outputs[0]?.split('.').pop() ?? '?';
                return (
                  <Typography key={s.stepId} variant="caption" sx={{ fontSize: 10, color: '#475569', display: 'block', lineHeight: 1.5 }}>
                    {s.operation}: → {tgtCol}
                  </Typography>
                );
              })}
            </StageCard>
            <StageArrow />
          </>
        )}

        {/* ── Stage Final: Target Table ── */}
        <StageCard
          color={TARGET_COLOR}
          icon={<TableChartIcon sx={{ fontSize: 13, color: TARGET_COLOR }} />}
          title={target.name}
        >
          <Typography variant="caption" sx={{ fontSize: 10, color: '#475569', display: 'block' }}>
            {target.stepCount} columns
          </Typography>
          <Typography variant="caption" sx={{ fontSize: 10, color: '#475569', display: 'block' }}>
            Avg confidence: {Math.round(target.avgConfidence * 100)}%
          </Typography>
        </StageCard>
      </Box>
    </Box>
  );
};

export default TargetChainPanel;
